import React, {useState} from "react";
import {Portal} from "@headlessui/react";
import {ConfirmDelete} from "../ConfirmModal";
import classNames from "classnames";
import {
    IconArrowNarrowDown,
    IconArrowNarrowUp,
    IconCircleMinus,
    IconCirclePlus,
    IconDownload,
    IconEdit,
    IconFiles,
    IconPlayerPlayFilled,
    IconTrash,
    IconZoom
} from "@tabler/icons-react";
import {TablerIconsType} from "@components/ComboButton";

export const Visibility: React.FC<{ visible: boolean, children: React.ReactNode }> = ({visible, children}) => {
    return <span className="flex justify-between" style={{visibility: visible ? "visible" : "hidden"}}>{children}</span>;
}

export interface TableButtonProps {
    className?: string,
    disabled?: boolean
}

interface DeleteButtonProps extends TableButtonProps {
    onCancel?: () => void,
    onDelete?: () => void,
    onConfirm?: () => void,
}

//https://stackoverflow.com/questions/35623656/how-can-i-display-a-modal-dialog-in-redux-that-performs-asynchronous-actions
export const Delete: React.FC<DeleteButtonProps> = (props) => {
    const [open, setOpen] = useState(false);
    if (props.onConfirm || props.onDelete)
        return <>
            <span className={classNames(props.className, "last:mr-2")}>
                {/*https://tailwindcss.com/docs/hover-focus-and-other-states#enabled*/}
                <button className="text-red-400 enabled:hover:text-red-600 disabled:opacity-25" disabled={props.disabled}
                        onClick={() => {
                            if (typeof props.onDelete === 'undefined')
                                setOpen(true);
                            else
                                props.onDelete();
                        }}>
                    <IconTrash className="h-5 w-5" aria-hidden="true"/>
                </button>
            </span>
            {open && (
                <Portal>
                    <ConfirmDelete
                        onCancel={() => {
                            setOpen(false);
                            if (props.onCancel)
                                props.onCancel();
                        }}
                        onConfirm={() => {
                            setOpen(false);
                            if (props.onConfirm)
                                props.onConfirm();
                        }}
                    />
                </Portal>
            )}
        </>
    else
        throw 'Devi specificare <onConfirm> oppure <onDelete>';
}

interface GenericButtonProps extends TableButtonProps {
    onAction: () => void,
}

export const Show: React.FC<GenericButtonProps> = ({onAction, className, disabled}) => {
    return (
        <span className={classNames(className, "last:mr-2")}>
            <button className="text-blue-400 enabled:hover:text-blue-600 disabled:opacity-25" disabled={disabled}
                    onClick={onAction}>
                <IconZoom className="h-5 w-5" aria-hidden="true"/>
            </button>
        </span>);
}

export const Edit: React.FC<GenericButtonProps> = ({onAction, className, disabled}) => {
    return (
        <span className={classNames(className, "last:mr-2")}>
            <button className="text-green-400 enabled:hover:text-green-600 disabled:opacity-25" disabled={disabled}
                    onClick={onAction}>
                <IconEdit className="h-5 w-5" aria-hidden="true"/>
            </button>
        </span>);
}

export const MoveUp: React.FC<GenericButtonProps> = ({onAction, className, disabled}) => {
    return (
        <span className={classNames(className, "last:mr-2")}>
            <button className="text-sky-400 enabled:hover:text-sky-600 disabled:opacity-50" disabled={disabled}
                    onClick={onAction}>
            <IconArrowNarrowUp className="h-5 w-5" aria-hidden="true"/>
            </button>
        </span>);
}

export const MoveDown: React.FC<GenericButtonProps> = ({onAction, className, disabled}) => {
    return (
        <span className={classNames(className, "last:mr-2")}>
            <button className="text-sky-400 enabled:hover:text-sky-600 disabled:opacity-50" disabled={disabled}
                    onClick={onAction}>
            <IconArrowNarrowDown className="h-5 w-5" aria-hidden="true"/>
            </button>
        </span>);
}

export const Download: React.FC<GenericButtonProps> = ({onAction, className, disabled}) => {

    return (
        <span className={classNames(className, "last:mr-2")}>
            <button className="text-sky-400 enabled:hover:text-sky-600 disabled:opacity-50" disabled={disabled}
                    onClick={onAction}>
            <IconDownload className="h-5 w-5" aria-hidden="true"/>
            </button>
        </span>);
}


export const Duplicate: React.FC<GenericButtonProps> = ({onAction, className, disabled}) => {
    return (
        <span className={classNames(className, "last:mr-2")}>
            <button className="text-sky-400 enabled:hover:text-sky-600 disabled:opacity-50" disabled={disabled}
                    onClick={onAction}>
            <IconFiles className="h-5 w-5" aria-hidden="true"/>
            </button>
        </span>);
}

export const GO: React.FC<GenericButtonProps> = ({onAction, className, disabled}) => {
    return (
        <span className={classNames(className, "last:mr-2")}>
            <button className="inline-flex text-green-500 enabled:hover:text-green-700 disabled:opacity-50"
                /*style={{textDecorationLine: 'none !important'}}*/
                    disabled={disabled}
                    onClick={onAction}>
            <IconPlayerPlayFilled className="h-5 w-4" aria-hidden="true"/>
                GO
            </button>
        </span>);
}

interface CustomButtonProps extends GenericButtonProps {
    Icon: TablerIconsType,
    btnClass?: string,
}

// <Custom Icon={MagnifyingGlassIcon} onAction={()=>show()}/>
export const Custom: React.FC<CustomButtonProps> = ({onAction, className, btnClass, disabled, Icon}) => {
    return (
        <span className={classNames(className, "last:mr-2")}>
            <button className={`inline-flex ${btnClass} disabled:opacity-50`} disabled={disabled}
                    onClick={onAction}>
            <Icon className="h-5 w-5" aria-hidden="true"/>
            </button>
        </span>);
}

interface ToggleButtonProps extends TableButtonProps {
    onToggle: (state: boolean) => void,
    initialState?: boolean,
}

export const ToggleDetail: React.FC<ToggleButtonProps> = ({onToggle, className, disabled, initialState = false}) => {
    const [state, setState] = useState<boolean>(initialState);
    const Icon = state ? IconCircleMinus : IconCirclePlus;
    return (
        <span className={classNames(className, "last:mr-2")}>
            <button className="inline-flex text-sky-500 enabled:hover:text-sky-700 disabled:opacity-25" disabled={disabled}
                    onClick={() => {
                        setState((state) => !state);
                        onToggle(!state);
                    }}>
                <Icon className="h-5 w-5" aria-hidden="true"/>
            </button>
        </span>);
}
